import { useEffect, useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { buildGetStatusCommand, buildSetMssPatientInfosCommand } from '../lib/commandBuilder'
import { sendEfficienceCommand } from '../lib/helpers'

type UseMssFlowParams = {
  iframe: HTMLIFrameElement | null
  isLoggedIn: boolean
  patientInsNIR?: string
  patientLastname?: string
  patientFirstname?: string
  mssClientMode?: boolean
}

/**
 * Sends patient identity traits (setMssPatientInfos) to Efficience when in MSS mode.
 * This allows Efficience to display patient name alongside MSS messages (spec §4.22).
 *
 * Also queries `getStatus` (spec §4.13) after login: its answer carries the
 * connected MSSanté mailbox address (`mss > email`), used as the sender address
 * for Reply-To (MSS.06) and DSN receipts (MSS.37). The answer is handled in
 * useMessageEvent via the returned `getStatusReqId`.
 */
export function useMssFlow({ iframe, isLoggedIn, patientInsNIR, patientLastname, patientFirstname, mssClientMode = false }: UseMssFlowParams) {
  const reqId = useMemo(() => uuidv4(), [])
  const getStatusReqId = useMemo(() => uuidv4(), [])

  useEffect(() => {
    if (!iframe?.contentWindow || !isLoggedIn || !mssClientMode) return
    if (!patientInsNIR || !patientLastname || !patientFirstname) return

    console.log('[MssFlow] Sending setMssPatientInfos command')
    const xml = buildSetMssPatientInfosCommand(reqId, [{ name: patientLastname, given: patientFirstname, ins: patientInsNIR }])
    sendEfficienceCommand(iframe, xml)
  }, [iframe, isLoggedIn, mssClientMode, patientInsNIR, patientLastname, patientFirstname, reqId])

  useEffect(() => {
    if (!iframe?.contentWindow || !isLoggedIn || !mssClientMode) return

    console.log('[MssFlow] Sending getStatus command')
    sendEfficienceCommand(iframe, buildGetStatusCommand(getStatusReqId))
  }, [iframe, isLoggedIn, mssClientMode, getStatusReqId])

  return { getStatusReqId }
}
